import { useState, useEffect } from "react"

// Simple module-level cache so navigating back doesn't re-fetch
const cache = new Map()

/**
 * Fetches the project list from /api/projects.
 * Optionally pass { category, featured } as params.
 */
export function useProjects(params = {}) {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
  ).toString()
  const url = `/api/projects${query ? `?${query}` : ""}`

  const [projects, setProjects] = useState(cache.get(url) || [])
  const [loading, setLoading]   = useState(!cache.has(url))
  const [error, setError]       = useState(null)

  useEffect(() => {
    if (cache.has(url)) return
    let cancelled = false
    setLoading(true)

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        if (cancelled) return
        cache.set(url, data)
        setProjects(data)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err.message)
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [url])

  return { projects, loading, error }
}

/**
 * Fetches a single project (with full detail) from /api/projects/:slug.
 */
export function useProject(slug) {
  const url = `/api/projects/${slug}`

  const [project, setProject] = useState(cache.get(url) || null)
  const [loading, setLoading]  = useState(!cache.has(url))
  const [error, setError]      = useState(null)

  useEffect(() => {
    if (!slug) return
    if (cache.has(url)) {
      setProject(cache.get(url))
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setProject(null)
    setError(null)

    fetch(url)
      .then((r) => {
        if (r.status === 404) throw new Error("not_found")
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        if (cancelled) return
        cache.set(url, data)
        setProject(data)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err.message)
        setLoading(false)
      })

    return () => { cancelled = true }
  }, [slug])

  return { project, loading, error }
}
